/**
 * Should create an rfc4122 v4 compliant UUID.
 * Taken from http://stackoverflow.com/a/2117523.
 */
export function createGUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0,
      v = c == 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function format(format: string, ...args: number[]): string {
  return format.replace(/{(\d+)}/g, function (match, index: string) {
    const i = Number.parseInt(index);
    return typeof args[i] != 'undefined' ? '' + args[i] : match;
  });
}

export function addGlobal(name: string, value: unknown): void {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let sh = (window as any).sh;
  if (!sh) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    sh = (window as any).sh = {};
  }
  sh[name] = value;
}

/* Combines two lists into a list of pairs, discarding trailing elements from the longer list */
export function zip<T, U>(t: T[], u: U[]): [T, U][] {
  const result: [T, U][] = [];
  for (let i = 0; i < Math.min(t.length, u.length); i++) {
    result.push([t[i], u[i]]);
  }

  return result;
}

export function distinct<T>(items: T[]): T[] {
  return Array.from(new Set<T>(items));
}

export function toMultimap<T>(
  keyFunc: (item: T) => string,
): (previousValue: { [key: string]: T[] }, currentValue: T) => { [key: string]: T[] } {
  return (prev, next) => {
    const key = keyFunc(next);
    return { ...prev, [key]: [...(prev[key] || []), next] };
  };
}

export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function sleepForever(): Promise<never> {
  return new Promise(() => undefined);
}

export function ifLoaded<T>(value: T | 'requested' | undefined): T | undefined {
  return value === 'requested' ? undefined : value;
}

export function isLoaded<T>(value: T | 'requested' | undefined): value is T {
  return ifLoaded(value) !== undefined;
}

export function toISODateString(dateString: string): string {
  return new Date(dateString).toISOString().split('T')[0];
}

export function clampOffset(offset: number, size: number, max: number, scale: number, padding: number): number {
  if (offset < padding) {
    return (padding - offset) / scale;
  }
  if (offset + size > max - padding) {
    return (max - padding - offset - size) / scale;
  }
  return 0;
}

export function flattenTree<T>(root: T, getChildren: (node: T) => T[] | undefined): T[] {
  const result = [] as T[];
  walkTree(root, getChildren, (x) => result.push(x));
  return result;
}

export function walkTree<T>(root: T, getChildren: (node: T) => T[] | undefined, callback: (node: T) => void): void {
  callback(root);
  getChildren(root)?.forEach((x) => walkTree(x, getChildren, callback));
}

export function sortBy<T>(array: T[], key: keyof T): T[] {
  return array.sort((a, b) => (a[key] < b[key] ? -1 : a[key] > b[key] ? 1 : 0));
}

export function numericStringToId(input: string): number {
  let hash = 5381;

  for (let i = 0; i < input.length; i++) {
    hash = (hash << 5) + hash + input.charCodeAt(i); // hash * 33 + char
    hash = hash >>> 0; // keep as unsigned 32-bit integer
  }

  return hash;
}
